import { Fragment, useId, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight,
  FlaskConical,
  Link2,
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import type {
  ProjectCoverageDomain,
  ProjectDataTableItem,
} from '@kirin/api-client/contracts/projects';
import { PolicyMode } from '@kirin/api-client/contracts/policies';
import { queryKeys } from '@/hooks/api/queryKeys';
import { cn } from '@/lib/utils';
import { fetchProjectCoverage } from './api';
import { CoverageBar, ModeBadge } from './coverageDisplay';
import { CoverageDrillList } from './CoverageDrillList';
import {
  groupCategoriesByStatus,
  STATUS_KEYS_ORDERED,
  STATUS_LABEL,
  type StatusKey,
} from './coverageTableView';

interface ProjectDrawerProps {
  row: ProjectDataTableItem;
  organizationId: string;
  /** Show the coverage-domain cards (AI instruction catalog flag). */
  showPolicyCoverage: boolean;
  /** Show the "Packages added by AI" card (packages flag). */
  showSupplyChain: boolean;
}

/** Tone per status bucket — the palette the counts have always used. */
const STATUS_TONE: Record<StatusKey, string> = {
  missing: 'text-red-600 dark:text-red-400',
  partial: 'text-amber-600 dark:text-amber-400',
  notScanned: 'text-muted-foreground',
  covered: 'text-green-600 dark:text-green-400',
};

/**
 * One catalog domain: title + mode + per-status counts + proportion bar. Each
 * non-zero count is a disclosure toggle that expands the specific categories
 * behind it (KIR-4811); the drawer's coverage query already holds every audited
 * category, so drilling in costs no extra request.
 *
 * Counts come from the `projects.coverage` query, whereas the row's "N gaps"
 * cell comes from `projects.dataTable` — where `notScanned` is derived by
 * subtraction rather than counted from absent rows. Those two queries have
 * independent staleness windows and can legitimately disagree; what matters is
 * that the counts here always match the list they open, so they are taken from
 * the bucketed categories rather than from `summary`.
 */
function DomainCard({
  domain,
  detailsHref,
}: {
  domain: ProjectCoverageDomain;
  detailsHref: string;
}): ReactElement {
  const { summary } = domain;
  const [openStatus, setOpenStatus] = useState<StatusKey | null>(null);
  const baseId = useId();
  const buckets = useMemo(
    () => groupCategoriesByStatus(domain.categories),
    [domain.categories]
  );

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-lg border p-3',
        // An open list needs the full drawer width to stay readable.
        openStatus && 'col-span-full'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium">{domain.title}</span>
        <ModeBadge mode={domain.mode} />
      </div>
      {summary.total === 0 ? (
        <p className="text-xs text-muted-foreground">
          No audited categories in this catalog.
        </p>
      ) : domain.categories.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
          {STATUS_KEYS_ORDERED.map((key, index) => {
            const count = buckets[key].length;
            const bucketLabel = STATUS_LABEL[key].toLowerCase();
            return (
              <Fragment key={key}>
                {/* Decorative: a reader should hear "6 missing, 2 partial". */}
                {index > 0 && <span aria-hidden="true">·</span>}
                {count === 0 ? (
                  // Nothing to drill into: plain text, so the tab order has no
                  // control that opens an empty panel.
                  <span className="text-muted-foreground">0 {bucketLabel}</span>
                ) : (
                  <button
                    type="button"
                    aria-expanded={openStatus === key}
                    aria-controls={`${baseId}-${key}`}
                    aria-label={`${count} ${bucketLabel} — show ${domain.title} categories`}
                    className={cn(
                      STATUS_TONE[key],
                      'underline decoration-dotted underline-offset-2'
                    )}
                    onClick={event => {
                      event.stopPropagation();
                      setOpenStatus(prev => (prev === key ? null : key));
                    }}
                  >
                    {count} {bucketLabel}
                  </button>
                )}
              </Fragment>
            );
          })}
        </div>
      ) : (
        // A non-zero summary with no category rows can't be drilled into (one
        // handler builds both, so this shouldn't happen) — fall back to static
        // text rather than render clickable zeroes.
        <div className="text-xs text-muted-foreground">
          <span className={STATUS_TONE.missing}>{summary.missing} missing</span>{' '}
          ·{' '}
          <span className={STATUS_TONE.partial}>{summary.partial} partial</span>{' '}
          ·{' '}
          <span className={STATUS_TONE.covered}>{summary.covered} covered</span>
        </div>
      )}
      <CoverageBar summary={summary} />
      {openStatus && (
        <CoverageDrillList
          id={`${baseId}-${openStatus}`}
          status={openStatus}
          categories={buckets[openStatus]}
          detailsHref={`${detailsHref}?catalog=${domain.id}&status=${openStatus}`}
        />
      )}
    </div>
  );
}

/** Supply-chain card mirroring the coverage cards' shape (KIR-3356 mockup). */
function SupplyChainCard({
  supplyChain,
}: {
  supplyChain: ProjectDataTableItem['supplyChain'];
}): ReactElement {
  const { malicious, vulnerable, unknown } = supplyChain;
  const clean = malicious === 0 && vulnerable === 0 && unknown === 0;
  return (
    <div className="flex flex-col gap-2 rounded-lg border p-3">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Packages added by AI</span>
      </div>
      {clean ? (
        <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
          <ShieldCheck className="h-3.5 w-3.5" />
          No malicious or vulnerable packages
        </div>
      ) : (
        <div className="flex flex-col gap-1 text-xs">
          {malicious > 0 && (
            <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
              <AlertTriangle className="h-3.5 w-3.5" />
              {malicious} malicious package{malicious === 1 ? '' : 's'}
            </span>
          )}
          {vulnerable > 0 && (
            <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
              <ShieldAlert className="h-3.5 w-3.5" />
              {vulnerable} vulnerable package{vulnerable === 1 ? '' : 's'}
            </span>
          )}
          {unknown > 0 && (
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5" />
              {unknown} package{unknown === 1 ? '' : 's'} still being checked
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Inventory row drawer (KIR-3356): a compact per-domain security-coverage
 * breakdown for the project — one card per AI-instruction catalog, plus a
 * supply-chain card and an LLM-vulnerability preview placeholder — with a link
 * to the full single-project page. Coverage is fetched on expand.
 */
export function ProjectDrawer({
  row,
  organizationId,
  showPolicyCoverage,
  showSupplyChain,
}: ProjectDrawerProps): ReactElement {
  // Only fetch coverage when the catalog flag is on — otherwise the endpoint
  // 404s and the packages card (row data, no fetch) must still render.
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.projects.coverage(organizationId, row.slug),
    queryFn: ({ signal }) =>
      fetchProjectCoverage(organizationId, row.slug, signal),
    staleTime: 30000,
    enabled: showPolicyCoverage,
  });

  const detailsHref = `/${organizationId}/inventory/projects/${encodeURIComponent(row.slug)}`;

  // Off-policy catalogs are excluded from the drawer (they still appear on the
  // full single-project coverage page); only Monitor/Enforce domains show here.
  const activeDomains =
    data?.domains.filter(domain => domain.mode !== PolicyMode.Off) ?? [];

  return (
    <div className="flex flex-col gap-3 bg-muted/30 p-4">
      {showPolicyCoverage && isLoading && (
        <p className="text-sm text-muted-foreground">Loading coverage…</p>
      )}
      {showPolicyCoverage && isError && (
        <p className="text-sm text-destructive">
          Couldn&apos;t load this project&apos;s security coverage.
        </p>
      )}
      <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {activeDomains.map(domain => (
          <DomainCard
            key={domain.id}
            domain={domain}
            detailsHref={detailsHref}
          />
        ))}
        {/* Row data, independent of the coverage fetch — renders even when the
            catalog flag is off and the coverage query is disabled/404. */}
        {showSupplyChain && <SupplyChainCard supplyChain={row.supplyChain} />}
        <div
          className={cn(
            'flex flex-col gap-2 rounded-lg border border-dashed p-3'
          )}
        >
          <div className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              LLM Vulnerability Discovery
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Preview — OpenAnt integration under construction.
          </p>
        </div>
      </div>
      <div className="flex justify-end">
        <Link
          to={detailsHref}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          Open project details
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
